import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './SuccessState.module.scss';
import HeroModel from '@/app/components/sections/Hero/HeroModel';
import Container from '@/app/components/layout/Container/Container';
import CloseModalButton from '@/app/components/ui/buttons/CloseModalButton/CloseModalButton';
import { toggleSiteScroll } from '@/lib/utils';
import { gsap } from '@/lib/gsapSetup';
import { useModal } from '@/context/ModalContext';

const SuccessState = ({ delay, isLoadModel, setIsSubmitted, title, text }) => {
  const wrapperRef = useRef(null);
  const solidRef = useRef(null);
  const modelRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { closeModal, setIsSuccessOpen } = useModal();

  const ease = 'expo.inOut';
  const duration = 2;

  useEffect(() => {
    setMounted(true);
    const timeout = setTimeout(() => {
      setIsVisible(true);
      closeModal();
    }, delay);

    return () => clearTimeout(timeout);
  }, [delay, closeModal]);

  useEffect(() => {
    if (
      isVisible &&
      wrapperRef.current &&
      solidRef.current &&
      modelRef.current
    ) {
      animateIn();
    }

    toggleSiteScroll(isVisible);
    setIsSuccessOpen(isVisible);
    return () => toggleSiteScroll(false);
  }, [isVisible]);

  const animateIn = () => {
    gsap.fromTo(wrapperRef.current, { y: '-110%' }, { y: 0, duration, ease });
    gsap.fromTo(solidRef.current, { y: '-110%' }, { y: 0, duration, ease });
    gsap.fromTo(
      modelRef.current,
      { y: '-180%' },
      { y: 0, duration: duration + 1.5, ease }
    );
  };

  const animateOut = (onComplete) => {
    gsap.to(wrapperRef.current, { y: '-110%', duration, ease, onComplete });
    gsap.to(solidRef.current, { y: '-110%', duration, ease });
    gsap.to(modelRef.current, { y: '-180%', duration, ease });
  };

  const handleClose = () => {
    animateOut(() => {
      setIsVisible(false);
      setIsSuccessOpen(false);
      setIsSubmitted(false);
    });
  };

  if (!mounted || !isVisible) return null;

  return createPortal(
    <>
      <div ref={solidRef} className={s.solid}></div>
      <div ref={wrapperRef} className={s.wrapper}>
        <CloseModalButton onClick={handleClose} />
        <Container className={s.container}>
          <div ref={modelRef} className={s.model}>
            {isLoadModel && (
              <HeroModel isVisible={true} sphereColor="#000000" />
            )}
          </div>
          <div className={s.content}>
            <span className="h2">{title}</span>
            {text && <p className="textGrey">{text}</p>}
          </div>
        </Container>
      </div>
    </>,
    document.getElementById('modals-root')
  );
};

export default SuccessState;
